import { useCallback, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getHappeningById, listHappenings } from '@littlebooker/dal';
import { saveShowSocialCards } from '@/lib/socialCards';
import { AsyncSearchableSelect, type SelectOption } from '@/components/AsyncSearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { isSocialCardsEnabled } from '@/config/features';
import { CardComposer } from '@/components/CardEditor/CardComposer';
import { StoryComposer } from '@/components/CardEditor/StoryComposer';
import { TakeoverComposer } from '@/components/CardEditor/TakeoverComposer';

type Format = 'post' | 'story' | 'takeover';

const UPCOMING_KEY = ['happenings', 'social-cards', 'upcoming'] as const;
const SELECTED_KEY = (id: string) => ['happening', 'social-cards', id] as const;

function showDate(h: { start_time?: string | null }) {
  return h.start_time ? new Date(h.start_time).toLocaleDateString() : '';
}

function showOption(h: { id: string; title?: string | null; start_time?: string | null }): SelectOption {
  return { id: h.id, name: h.title || 'Untitled show', subtitle: showDate(h) };
}

export default function SocialCardsPage() {
  const { account } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('happening') ?? '';
  const [format, setFormat] = useState<Format>('post');

  // The selected show is fetched by id rather than found in a full list, so the
  // picker never has to hold every happening in memory.
  const { data: selectedShow = null } = useQuery({
    queryKey: SELECTED_KEY(selectedId),
    queryFn: () => getHappeningById(selectedId),
    enabled: Boolean(selectedId),
  });

  // Upcoming shows fill the dropdown before anything is typed — the common case
  // is announcing a show that is a week or two out.
  const { data: upcomingShows = [] } = useQuery({
    queryKey: UPCOMING_KEY,
    queryFn: () =>
      listHappenings({
        start_time_gte: new Date().toISOString(),
        limit: 15,
        withParticipants: false,
      }),
  });

  const searchShows = useCallback(async (query: string): Promise<SelectOption[]> => {
    const results = await listHappenings({ query, limit: 25, order: 'desc', withParticipants: false });
    return results.map(showOption);
  }, []);

  const pickerOptions = useMemo(() => {
    const options = upcomingShows.map(showOption);
    if (selectedShow && !options.some((o) => o.id === selectedShow.id)) {
      options.unshift(showOption(selectedShow));
    }
    return options;
  }, [upcomingShows, selectedShow]);

  const setSelectedId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('happening', id);
    else next.delete('happening');
    setSearchParams(next, { replace: true });
  };

  const handleUploaded = async (url: string, templateId: string) => {
    if (!selectedShow) return; // save disabled without a show; guard anyway
    const existing = selectedShow.metadata?.social_cards ?? {};
    await saveShowSocialCards(selectedShow.id, { ...existing, [templateId]: url }, existing);
    await queryClient.invalidateQueries({ queryKey: SELECTED_KEY(selectedShow.id) });
  };

  const savedCards = selectedShow?.metadata?.social_cards ?? {};

  // Radio-Weesper-specific section; other accounts can't reach it directly.
  // (After all hooks, so hook order stays stable.)
  if (!isSocialCardsEnabled(account?.slug)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Social Cards</h1>
        <p className="text-sm text-muted-foreground">
          Build announcement graphics, true to the Figma design.
        </p>
      </div>

      {/* Format: single-show post vs multi-show Instagram Story */}
      <div className="mb-6">
        <div className="inline-flex rounded-md border border-input p-0.5">
          {(['post', 'story', 'takeover'] as Format[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFormat(f)}
              className={`rounded px-4 py-1.5 text-sm transition-colors ${
                format === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f === 'post' ? 'Post' : f === 'story' ? 'Story (line-up)' : 'Takeover'}
            </button>
          ))}
        </div>
      </div>

      {format === 'post' ? (
        <>
          <div className="mb-6 max-w-md">
            <div className="mb-1.5 flex items-baseline justify-between">
              <label className="block text-sm font-medium">Show</label>
              {selectedId && (
                <button
                  type="button"
                  onClick={() => setSelectedId('')}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </div>
            <AsyncSearchableSelect
              searchFn={searchShows}
              selectedIds={selectedId ? [selectedId] : []}
              initialOptions={pickerOptions}
              onSelect={(option) => setSelectedId(option.id)}
              placeholder="Select a show to attach the card to..."
              searchPlaceholder="Search shows by title..."
              emptyMessage="No shows found"
              showSelectedInList
            />
            {selectedShow && (
              <p className="mt-1.5 text-xs text-muted-foreground">{showDate(selectedShow)}</p>
            )}
            {selectedShow && Object.keys(savedCards).length > 0 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Already saved: {Object.keys(savedCards).join(', ')}
              </p>
            )}
          </div>

          <CardComposer
            // Remount when the show changes so saved-state (and the resident
            // default derived from the show type) is applied fresh.
            key={selectedId || 'none'}
            accountSlug={account?.slug}
            initialVariant={selectedShow?.metadata?.show_type === 'resident' ? 'resident' : 'guest'}
            onUploaded={handleUploaded}
            saveLabel="Save to show"
            saveDisabled={!selectedShow}
            saveDisabledHint="Pick a show above to attach the card (you can still Download without one)."
          />
        </>
      ) : format === 'story' ? (
        <StoryComposer accountSlug={account?.slug} />
      ) : (
        <TakeoverComposer accountSlug={account?.slug} />
      )}
    </div>
  );
}
